import { FinishedGameSummary, getPlayerDisplayName } from "@mancala/core";

type Translate = (key: string, options?: Record<string, unknown>) => string;

const PLAYER1_STORE = 6;
const PLAYER2_STORE = 13;

// The result as the viewer would say it: "You won · Opponent left" for a player, "alice won" for anyone else.
export function getResultText(game: FinishedGameSummary, viewerId: string | undefined, t: Translate): string {
    if (game.result === null) return t("Abandoned");
    if (game.result === "draw") return t("Draw");
    const winner = game.result === "player1" ? game.player1 : game.player2;
    const viewerSeat = getViewerSeat(game, viewerId);
    if (!viewerSeat) return t("PlayerWon", { name: getPlayerDisplayName(winner, t) ?? t("Anonymous") });

    const viewerWon = winner.userId === viewerId;
    const outcome = viewerWon ? t("YouWon") : t("YouLost");
    if (game.endReason === "left") return `${outcome} · ${viewerWon ? t("OpponentLeft") : t("YouLeft")}`;
    if (game.endReason === "timeout") return `${outcome} · ${viewerWon ? t("OpponentTimedOut") : t("YouTimedOut")}`;
    return outcome;
}

// Class of the result's pill: "win" when someone won (and the viewer did not lose), "lose" when the viewer lost.
export function getResultPillClass(game: FinishedGameSummary, viewerId: string | undefined): string {
    if (game.result === null || game.result === "draw") return "";
    const viewerSeat = getViewerSeat(game, viewerId);
    return viewerSeat && viewerSeat !== game.result ? "lose" : "win";
}

// Final stones in the stores, the viewer's first when they played.
export function getScoreText(game: FinishedGameSummary, viewerId: string | undefined): string {
    const player1 = game.board[PLAYER1_STORE], player2 = game.board[PLAYER2_STORE];
    return getViewerSeat(game, viewerId) === "player2" ? `${player2} - ${player1}` : `${player1} - ${player2}`;
}

export function getViewerSeat(game: FinishedGameSummary, viewerId: string | undefined): "player1" | "player2" | undefined {
    if (viewerId === undefined) return undefined;
    if (game.player1.userId === viewerId) return "player1";
    if (game.player2.userId === viewerId) return "player2";
    return undefined;
}

export function formatDate(epochMillis: number, language: string): string {
    return new Date(epochMillis).toLocaleDateString(language, { day: "numeric", month: "short", year: "numeric" });
}

// "12:05" or "1:02:05"
export function formatDuration(milliseconds: number): string {
    const totalSeconds = Math.max(0, Math.round(milliseconds / 1000));
    const hours = Math.floor(totalSeconds / 3600), minutes = Math.floor(totalSeconds / 60) % 60, seconds = totalSeconds % 60;
    const pad = (n: number) => String(n).padStart(2, "0");
    return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}
